/**
 * CableBoxPlacementOverlay
 *
 * Overlay component for placing standalone junction boxes (without drawing paths).
 * Click on the canvas to place a junction box at that location.
 */

import React, { useState, useCallback, useRef } from 'react';
import { useAppStore } from '@/stores/appStore';
import { useFloorPlanContext } from '../../FloorPlanProvider';
import { useCableJunctionsStandardized } from '@/hooks/entities/useCableJunctionsStandardized';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import type { Point } from '@/types/common';

interface CableBoxPlacementOverlayProps {
  containerRef: React.RefObject<HTMLDivElement>;
  zoom: number;
  position: { x: number; y: number };
}

export function CableBoxPlacementOverlay({
  containerRef,
  zoom,
  position,
}: CableBoxPlacementOverlayProps) {
  // Track processing state to prevent double-clicks
  const [isProcessing, setIsProcessing] = useState(false);

  // Get floor plan context
  const { floorPlan, data } = useFloorPlanContext();
  const floorPlanId = floorPlan.id;

  // Get default junction scale from floor plan metadata
  const markerSizes = data.floorPlanMetadata?.marker_sizes as {
    junction_default_scale?: number;
  } | null;
  const defaultJunctionScale = markerSizes?.junction_default_scale ?? 1.0;

  // Use ref to track current default scale to avoid stale closure issues
  const defaultJunctionScaleRef = useRef(defaultJunctionScale);
  defaultJunctionScaleRef.current = defaultJunctionScale;

  // Get junction operations
  const { createJunction } = useCableJunctionsStandardized(floorPlanId);

  // Get store actions
  const activeMode = useAppStore(state => state.ui.toolbar.activeMode);
  const selectEntity = useAppStore(state => state.selectEntity);
  const setActiveDrawer = useAppStore(state => state.setActiveDrawer);

  // Check if we're in cable junction placement mode
  const isJunctionPlacementMode = activeMode === 'cable-junction-placement';

  // Convert screen coordinates to floor plan coordinates
  const screenToFloorPlanCoords = useCallback(
    (clientX: number, clientY: number): Point | null => {
      if (!containerRef.current) return null;
      const rect = containerRef.current.getBoundingClientRect();
      const x = (clientX - rect.left - position.x) / zoom;
      const y = (clientY - rect.top - position.y) / zoom;
      return { x, y };
    },
    [containerRef, position, zoom],
  );

  // Handle click to place junction box
  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      if (!isJunctionPlacementMode || isProcessing) return;

      // Prevent the click from propagating
      e.stopPropagation();

      // Get the click position in floor plan coordinates
      const point = screenToFloorPlanCoords(e.clientX, e.clientY);
      if (!point) return;

      setIsProcessing(true);

      try {
        // Create the junction box at the clicked position
        const junction = await createJunction(
          {
            floor_plan_id: floorPlanId,
            x: point.x,
            y: point.y,
            scale: defaultJunctionScaleRef.current,
          },
          { showSuccessToast: false },
        );

        if (junction) {
          // Select the new junction and open the drawer
          selectEntity('cableJunction', junction.id);
          setActiveDrawer('cableJunction');
        }
      } catch (error) {
        logger.error('Failed to create junction box:', error);
        toast.error('Failed to create junction box');
      } finally {
        setIsProcessing(false);
      }
    },
    [
      isJunctionPlacementMode,
      isProcessing,
      screenToFloorPlanCoords,
      createJunction,
      floorPlanId,
      selectEntity,
      setActiveDrawer,
    ],
  );

  // Don't render if not in junction placement mode
  if (!isJunctionPlacementMode) {
    return null;
  }

  return (
    <div
      className="absolute inset-0 cursor-crosshair"
      style={{ zIndex: 100 }}
      onClick={handleClick}
    />
  );
}
